import { History, X } from "lucide-react";
import { AgentHistoryItem } from "../../types/dashboard";
import { useState, useEffect } from "react";
import AgentHistoryCard from "./AgentHistoryCard";
import AgentHistoryModal from "./AgentHistoryModal";

interface HistoryModalProps {
  userHistory: AgentHistoryItem[];
  onClose: () => void;
  isLoading?: boolean;
}

export default function HistoryModal({
  userHistory,
  onClose,
  isLoading = false,
}: HistoryModalProps) {
  const [historyItems, setHistoryItems] = useState<AgentHistoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<AgentHistoryItem | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setHistoryItems(userHistory);
  }, [userHistory]);

  const handleHistoryItemClick = (item: AgentHistoryItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        onClick={handleBackgroundClick}
      >
        <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl h-[600px] lg:h-[800px] w-full max-w-4xl flex flex-col">
          <div className="flex items-center justify-between p-4 lg:p-6 pb-3 lg:pb-4 flex-shrink-0 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 lg:w-6 lg:h-6 text-purple-400" />
              <h3 className="text-lg lg:text-xl font-semibold text-white">
                사용 히스토리
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden px-4 lg:px-6 pb-4 lg:pb-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent mb-4"></div>
                <p className="text-slate-300">히스토리를 불러오는 중...</p>
              </div>
            ) : historyItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-4">
                <History className="w-10 h-10 lg:w-12 lg:h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-300 text-base lg:text-lg text-center">
                  아직 사용한 에이전트가 없습니다.
                </p>
                <p className="text-slate-500 text-sm mt-2 text-center">
                  AI Studio에서 다양한 에이전트를 체험해보세요!
                </p>
              </div>
            ) : (
              <div className="h-full overflow-y-auto elegant-scrollbar pt-4 lg:pt-6">
                <div className="space-y-4 pr-2">
                  {historyItems.map((item) => (
                    <AgentHistoryCard
                      key={item.id}
                      item={item}
                      onClick={() => handleHistoryItemClick(item)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AgentHistoryModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
